/**
 * Session context — handles initial auth flow.
 *  - Inside Telegram: POST initData to /auth/telegram.
 *  - Outside Telegram: POST to /auth/dev (only available when API ENVIRONMENT=dev).
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Me } from '@waylo/shared';
import {
  authDev,
  authWithTelegram,
  getMe,
  loadSessionToken,
  setSessionToken as setSessionTokenViaApi,
} from './api';
import { getWebApp } from './telegram';

interface SessionState {
  status: 'loading' | 'authenticated' | 'error' | 'unauthenticated';
  me: Me | null;
  error: string | null;
  /** Diagnostic info so the user-facing error splash can show why auth failed. */
  diagnostic: {
    inTelegram: boolean;
    initDataLength: number;
    tgVersion: string | null;
    tgPlatform: string | null;
  } | null;
  refresh: () => Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

/**
 * Reads a referral code from `?ref=...` in the URL search or hash. Returns
 * the raw string (uppercased+trimmed) or null. Server will normalize.
 */
function readRefFromUrl(): string | null {
  try {
    const search = new URLSearchParams(window.location.search);
    const fromSearch = search.get('ref');
    if (fromSearch) return fromSearch;
    const hashQ = window.location.hash.split('?')[1];
    if (hashQ) {
      const hp = new URLSearchParams(hashQ);
      const fromHash = hp.get('ref');
      if (fromHash) return fromHash;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<SessionState['status']>('loading');
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [diagnostic, setDiagnostic] = useState<SessionState['diagnostic']>(null);

  const refresh = async (): Promise<void> => {
    try {
      const me = await getMe();
      setMe(me);
      setStatus('authenticated');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStatus('error');
    }
  };

  useEffect(() => {
    const run = async (): Promise<void> => {
      const tg = getWebApp();
      const inTelegram = Boolean(tg);
      const diag: SessionState['diagnostic'] = {
        inTelegram,
        initDataLength: tg?.initData?.length ?? 0,
        tgVersion: (tg as unknown as { version?: string })?.version ?? null,
        tgPlatform: (tg as unknown as { platform?: string })?.platform ?? null,
      };
      setDiagnostic(diag);

      const urlRef = readRefFromUrl();

      // 1) Telegram initData path — fresh signature on every Mini App launch.
      if (tg?.initData) {
        try {
          const r = await authWithTelegram(tg.initData, urlRef ?? undefined);
          setMe(r.me);
          setStatus('authenticated');
          return;
        } catch (e) {
          // Inside Telegram with present initData → this is the real auth path.
          // If it fails, do NOT fall through to authDev — that hides the real bug
          // behind a confusing "dev auth disabled" message. Surface it directly.
          console.warn('[waylo] authWithTelegram failed', e);
          setSessionTokenViaApi(null);
          setError(e instanceof Error ? e.message : String(e));
          setStatus('error');
          return;
        }
      }

      // 2) We have a stored session token from a previous browser/Telegram run.
      if (loadSessionToken()) {
        try {
          const me = await getMe();
          setMe(me);
          setStatus('authenticated');
          return;
        } catch (e) {
          console.warn('[waylo] stored session invalid, clearing', e);
          setSessionTokenViaApi(null);
        }
      }

      // 3) If we're INSIDE Telegram but somehow have no initData (very rare —
      // points to a misconfigured menu button or ancient client), surface that
      // explicitly instead of trying authDev which will 403 with a misleading message.
      if (inTelegram) {
        setError('Telegram did not provide initData. Try reopening via @WayloRide_bot menu button, or update Telegram.');
        setStatus('error');
        return;
      }

      // 4) Browser path: try dev-auth (locked in production → shows friendly splash).
      try {
        const r = await authDev();
        setMe(r.me);
        setStatus('authenticated');
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setStatus('error');
      }
    };
    void run();
  }, []);

  return (
    <Ctx.Provider value={{ status, me, error, diagnostic, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession must be used inside <SessionProvider>');
  return v;
}
